/* corner-configurator.js — sibling of configurator.js, scoped to corner shelves.
 *
 * Reads window.CORNER_CONFIG (emitted by corner-configurator-config.liquid).
 * Talks to the same backend as the slab configurator (/api/price + /api/configure)
 * using subtype=corner.
 *
 * Init pattern mirrors slab: the IIFE registers DOMContentLoaded /
 * shopify:section:load listeners so init happens AFTER both the inline
 * config script and the <corner-configurator> element have been parsed.
 * script_tag emits a non-deferred <script src=>, so executing init at
 * parse time would race the inline config script and find it undefined.
 *
 * Coexistence with the slab configurator:
 *   - Distinct form id (corner-form-<product>-<section>) so submits don't race.
 *   - URL state under namespaced keys (cs/ct/cf) so slab params and corner
 *     params live in the same URL without collision.
 *   - Reads window.SLAB_CONFIG.currentMaterial when available; falls back to
 *     CORNER_CONFIG.currentMaterial when slab block is absent.
 */

'use strict';

(function () {
  if (window.__cornerConfiguratorLoaded) return;
  window.__cornerConfiguratorLoaded = true;

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);
  if (document.readyState !== 'loading') initAll();

  function initAll() {
    document.querySelectorAll('corner-configurator').forEach(initOne);
  }

  function initOne(root) {
    if (root.__cnrInitialized) return;
    root.__cnrInitialized = true;
    try { initRoot(root); }
    catch (err) { console.error('[corner-configurator] init failed:', err); }
  }

  function initRoot(root) {
    const cfg = window.CORNER_CONFIG;
    if (!cfg) { console.error('[corner-configurator] CORNER_CONFIG missing'); return; }

    function currentMaterial() {
      if (window.SLAB_CONFIG && window.SLAB_CONFIG.currentMaterial) {
        return window.SLAB_CONFIG.currentMaterial;
      }
      return cfg.currentMaterial;
    }

    const $form      = root.querySelector('form');
    const $thickSel  = root.querySelector('[data-cnr="thickness"]');
    const $thickExactRow = root.querySelector('[data-cnr="thickness-exact-row"]');
    const $thickExactW   = root.querySelector('[data-cnr="thickness-exact-w"]');
    const $thickExactF   = root.querySelector('[data-cnr="thickness-exact-f"]');
    const $aW        = root.querySelector('[data-cnr="a-w"]');
    const $aF        = root.querySelector('[data-cnr="a-f"]');
    const $bW        = root.querySelector('[data-cnr="b-w"]');
    const $bF        = root.querySelector('[data-cnr="b-f"]');
    const $cW        = root.querySelector('[data-cnr="c-w"]');
    const $cF        = root.querySelector('[data-cnr="c-f"]');
    const $finList   = root.querySelector('[data-cnr="finish-list"]');
    const $buildList = root.querySelector('[data-cnr="build-styles"]');
    const $price     = root.querySelector('[data-cnr="price"]');
    const $status    = root.querySelector('[data-cnr="status"]');
    const $variant   = root.querySelector('[data-cnr="variant-id"]');
    const $atc       = root.querySelector('[data-cnr="atc"]');
    const $guideLink = root.querySelector('[data-cnr="guidance-link"]');

    if (!$form || !$thickSel || !$aW || !$aF || !$bW || !$bF || !$cW || !$cF
        || !$finList || !$buildList) {
      console.error('[corner-configurator] required elements missing');
      return;
    }

    // Wire the over-size guidance link href from CORNER_CONFIG so URL
    // changes ship as a config edit, not a Liquid edit.
    if ($guideLink && cfg.guidanceUrl) {
      $guideLink.setAttribute('href', cfg.guidanceUrl);
      $guideLink.setAttribute('target', '_blank');
      $guideLink.setAttribute('rel', 'noopener');
    }

    // ── State ────────────────────────────────────────────────────────
    // A/B/C are stored as whole-inch + fraction-string pairs so the
    // dropdowns can round-trip exactly. Numeric values are derived via
    // dimInches() on demand (for API calls + line item properties).
    const defaultBuild = (cfg.buildStyles && (cfg.buildStyles.find(b => b.isDefault) || cfg.buildStyles[0])) || null;
    const state = {
      thickness:    (cfg.thicknesses[0] || {}).code || '',
      thickExactW:  '',
      thickExactF:  '',
      aWhole:     String(cfg.aRange.min),
      aFraction:  '0',
      bWhole:     String(cfg.bRange.min),
      bFraction:  '0',
      cWhole:     String(cfg.cRange.min),
      cFraction:  '0',
      finish:     'finished',
      finishChip: 'clear-matte',
      buildStyle: defaultBuild ? defaultBuild.id : '',
    };

    // ── Dimension helpers ────────────────────────────────────────────
    function fractionToDecimal(s) {
      if (!s || s === '0') return 0;
      const parts = String(s).split('/').map(Number);
      if (parts.length !== 2 || !parts[1]) return 0;
      return parts[0] / parts[1];
    }
    function dimInches(whole, frac) {
      const w = parseInt(whole, 10);
      if (!Number.isFinite(w)) return null;
      return w + fractionToDecimal(frac);
    }
    function formatDim(whole, frac) {
      // "20", "20 1/2", "12 3/4" — workshop-readable, matches slab's encoding.
      if (!frac || frac === '0') return whole + '"';
      return whole + ' ' + frac + '"';
    }

    function fillRangeSelect(sel, min, max) {
      const out = [];
      for (let i = min; i <= max; i++) out.push('<option value="' + i + '">' + i + '</option>');
      sel.innerHTML = out.join('');
    }
    function fillFractions(sel) {
      sel.innerHTML = cfg.fractions.map(f => (
        '<option value="' + esc(f) + '">' + esc(f === '0' ? '0' : f) + '</option>'
      )).join('');
    }

    // Ceiling-fraction rule (Billy 2026-06-09): when the whole-inch
    // value sits at the configured max, lock the fraction dropdown to
    // "0" so the customer can't pick a value above the ceiling. Mirrors
    // the slab helper of the same name. `floorExclusive=true` is for
    // ranges like 4.1-6 where whole=4 + frac=0 = 4.0 (below the 4.1
    // floor) — then the "0" option is disabled too. A/B/C all pass
    // floorExclusive=false (integer floors).
    function applyCeilingRule(wholeSel, fracSel, minWhole, maxWhole, floorExclusive) {
      if (!wholeSel || !fracSel) return;
      const whole = wholeSel.value;
      const zeroOpt = fracSel.querySelector('option[value="0"]');
      if (whole === String(maxWhole)) {
        fracSel.value = '0';
        fracSel.disabled = true;
        if (zeroOpt) zeroOpt.disabled = false;
      } else if (floorExclusive && whole === String(minWhole)) {
        fracSel.disabled = false;
        if (zeroOpt) zeroOpt.disabled = true;
        if (fracSel.value === '0') fracSel.value = '1/16';
      } else {
        fracSel.disabled = false;
        if (zeroOpt) zeroOpt.disabled = false;
      }
    }
    function applyAllCeilingRules() {
      applyCeilingRule($aW, $aF, cfg.aRange.min, cfg.aRange.max, false);
      applyCeilingRule($bW, $bF, cfg.bRange.min, cfg.bRange.max, false);
      applyCeilingRule($cW, $cF, cfg.cRange.min, cfg.cRange.max, false);
    }

    // Range-thickness handling (Billy 2026-06-09): when the customer
    // picks a range thickness (e.g. "2-4"), surface the "Exact (order
    // record)" secondary row so the workshop has the precise value.
    // Pricing keys off the range code; the exact value is a line item
    // property only.
    function parseThicknessRange(code) {
      if (!code || !code.includes('-')) return null;
      const parts = code.split('-').map(parseFloat);
      if (parts.length !== 2 || !parts.every(Number.isFinite)) return null;
      return { low: parts[0], high: parts[1] };
    }
    function refreshThicknessExact() {
      if (!$thickExactRow) return;
      const range = parseThicknessRange(state.thickness);
      if (!range) {
        $thickExactRow.hidden = true;
        state.thickExactW = '';
        state.thickExactF = '';
        return;
      }
      $thickExactRow.hidden = false;
      const minWhole = Math.floor(range.low);
      const maxWhole = Math.floor(range.high);
      fillRangeSelect($thickExactW, minWhole, maxWhole);
      fillFractions($thickExactF);
      // Preserve a still-valid prior choice, else default to the floor.
      if (!state.thickExactW || +state.thickExactW < minWhole || +state.thickExactW > maxWhole) {
        state.thickExactW = String(minWhole);
        state.thickExactF = '0';
      }
      $thickExactW.value = state.thickExactW;
      $thickExactF.value = state.thickExactF;
      // floorExclusive: ranges like "4.1-6" have a non-integer floor; the
      // discrete whole=4 + frac=0 sub-position is below the floor.
      const floorExclusive = (range.low > Math.floor(range.low));
      applyCeilingRule($thickExactW, $thickExactF, minWhole, maxWhole, floorExclusive);
    }
    function thicknessExactLabel() {
      if (!state.thickExactW) return null;
      if (!state.thickExactF || state.thickExactF === '0') return state.thickExactW + '"';
      return state.thickExactW + ' ' + state.thickExactF + '"';
    }

    // ── Finish rebuild ──────────────────────────────────────────────
    // Two gates:
    //   1. allowedFinishesByMaterial — DB-derived (Billy 2026-06-13). A
    //      chip is offered iff a corner_pricing row exists for the host
    //      material's tier + the chip's DB finish enum. Falls back to
    //      "allow everything" when the map is empty (fresh env, no
    //      import yet) so the chip list is never blank on first load.
    //   2. requiresRubioAllowed — legacy metaobject Rubio gate, kept
    //      as a belt-and-braces layer on top of the DB gate.
    // Only one chip is checked at a time (acts as a radio group); the
    // change handler below resets the siblings to keep that invariant.
    function rebuildFinishOptions() {
      const mat = currentMaterial();
      const rubioOk = !!(mat && mat.rubioAllowed);
      const allowedMap = cfg.allowedFinishesByMaterial || {};
      const matKey = mat && mat.tier != null ? String(mat.tier) : null;
      const allowedForMat = matKey && allowedMap[matKey];

      const allowed = cfg.finishOptions.filter(opt => {
        if (opt.requiresRubioAllowed && !rubioOk) return false;
        if (Array.isArray(allowedForMat) && allowedForMat.length) {
          return allowedForMat.includes(opt.dbFinish);
        }
        return true; // empty map → permissive fallback
      });

      // Safety net: if every chip got filtered out (misconfigured CSV,
      // no rows for this material), fall back to the unfiltered list so
      // the customer can still pick something and the backend 404 gives
      // a clear error trail.
      const finalChips = allowed.length ? allowed : cfg.finishOptions.slice();
      const allowedIds = finalChips.map(o => o.id);

      if (!allowedIds.includes(state.finishChip)) {
        const def = finalChips.find(o => o.isDefault) || finalChips[0];
        state.finishChip = def.id;
        state.finish     = def.finish;
      }

      $finList.innerHTML = finalChips.map(opt => {
        const checked = (opt.id === state.finishChip) ? ' checked' : '';
        return (
          '<label class="cnr-chip" data-cnr-finish="' + esc(opt.id) + '" data-finish="' + esc(opt.finish) + '">'
            + '<span>' + esc(opt.label) + '</span>'
            + '<input type="checkbox" data-cnr-finish-input value="' + esc(opt.id) + '"' + checked + '>'
          + '</label>'
        );
      }).join('');
    }

    function esc(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({
        '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;',
      })[c]);
    }

    // ── URL state ────────────────────────────────────────────────────
    // `cs` (legacy Size) is intentionally dropped; URLs from the old
    // shape simply restore to defaults, which is the same UX customers
    // see when navigating to a material page for the first time.
    const URL_KEYS = {
      thickness:    'ct',
      thickExactW:  'ctw',
      thickExactF:  'ctf',
      finishChip:   'cf',
      aWhole:       'ca',
      aFraction:    'caf',
      bWhole:       'cb',
      bFraction:    'cbf',
      cWhole:       'cc',
      cFraction:    'ccf',
      buildStyle:   'cbs',
    };

    function inRange(v, min, max) {
      const n = parseInt(v, 10);
      return Number.isFinite(n) && n >= min && n <= max;
    }
    function isFraction(v) {
      return cfg.fractions.includes(v);
    }

    function readStateFromURL() {
      const p = new URLSearchParams(location.search);
      const ct = p.get(URL_KEYS.thickness);
      const ctw = p.get(URL_KEYS.thickExactW);
      const ctf = p.get(URL_KEYS.thickExactF);
      const cf = p.get(URL_KEYS.finishChip);
      if (ct && cfg.thicknesses.some(t => t.code === ct)) state.thickness = ct;
      if (ctw) state.thickExactW = ctw;
      if (ctf && isFraction(ctf)) state.thickExactF = ctf;
      if (cf) {
        const opt = cfg.finishOptions.find(o => o.id === cf);
        if (opt) { state.finishChip = opt.id; state.finish = opt.finish; }
      }

      const ca  = p.get(URL_KEYS.aWhole);
      const caf = p.get(URL_KEYS.aFraction);
      const cb  = p.get(URL_KEYS.bWhole);
      const cbf = p.get(URL_KEYS.bFraction);
      const cc  = p.get(URL_KEYS.cWhole);
      const ccf = p.get(URL_KEYS.cFraction);
      if (inRange(ca,  cfg.aRange.min, cfg.aRange.max)) state.aWhole    = ca;
      if (isFraction(caf))                               state.aFraction = caf;
      if (inRange(cb,  cfg.bRange.min, cfg.bRange.max)) state.bWhole    = cb;
      if (isFraction(cbf))                               state.bFraction = cbf;
      if (inRange(cc,  cfg.cRange.min, cfg.cRange.max)) state.cWhole    = cc;
      if (isFraction(ccf))                               state.cFraction = ccf;

      const cbs = p.get(URL_KEYS.buildStyle);
      if (cbs && cfg.buildStyles.some(b => b.id === cbs)) state.buildStyle = cbs;
    }

    function pushStateToURL() {
      const p = new URLSearchParams(location.search);
      p.set(URL_KEYS.thickness,  state.thickness);
      p.set(URL_KEYS.finishChip, state.finishChip);
      p.set(URL_KEYS.aWhole,     state.aWhole);
      p.set(URL_KEYS.aFraction,  state.aFraction);
      p.set(URL_KEYS.bWhole,     state.bWhole);
      p.set(URL_KEYS.bFraction,  state.bFraction);
      p.set(URL_KEYS.cWhole,     state.cWhole);
      p.set(URL_KEYS.cFraction,  state.cFraction);
      if (state.thickExactW) p.set(URL_KEYS.thickExactW, state.thickExactW); else p.delete(URL_KEYS.thickExactW);
      if (state.thickExactF) p.set(URL_KEYS.thickExactF, state.thickExactF); else p.delete(URL_KEYS.thickExactF);
      if (state.buildStyle) p.set(URL_KEYS.buildStyle, state.buildStyle);
      // Clean any leftover legacy `cs` key from old share links.
      p.delete('cs');
      history.replaceState(null, '', location.pathname + '?' + p.toString());
    }

    // ── Pricing ──────────────────────────────────────────────────────
    let priceSeq = 0;
    let priceTimer = null;

    function schedulePrice() {
      clearTimeout(priceTimer);
      priceTimer = setTimeout(fetchPrice, 200);
    }

    async function fetchPrice() {
      const mat = currentMaterial();
      if (!mat) {
        $price.textContent = '—';
        $status.textContent = 'Material not configured for this page.';
        return;
      }

      const aIn = dimInches(state.aWhole, state.aFraction);
      const bIn = dimInches(state.bWhole, state.bFraction);
      const cIn = dimInches(state.cWhole, state.cFraction);
      if (aIn == null || bIn == null || cIn == null) {
        $price.textContent = '—';
        $status.textContent = 'Please complete the A, B, C dimensions.';
        return;
      }

      const mySeq = ++priceSeq;
      const params = new URLSearchParams({
        subtype:       'corner',
        product_id:    cfg.productId,
        // Slab convention: "material type <tier>" — corner_pricing rows
        // are imported in that exact format, so the WHERE-equals lookup
        // needs the prefix.
        material:      'material type ' + mat.tier,
        material_name: mat.name,
        finish:        state.finish,
        a_inches:      String(aIn),
        b_inches:      String(bIn),
        thickness:     state.thickness,
        // C is sent for visibility but ignored by the price lookup; the
        // backend recognises it only at /api/configure for the variant
        // title + line item properties (workshop metadata).
        c_inches:      String(cIn),
      });

      try {
        const res = await fetch(cfg.apiUrl + '/api/price?' + params.toString(), {
          headers: { 'Accept': 'application/json' },
        });
        if (mySeq !== priceSeq) return;
        const data = await res.json().catch(() => null);
        if (!res.ok || !data) {
          $price.textContent = '—';
          $status.textContent = (data && data.error) ? errorMessage(data.error) : 'Price unavailable.';
          return;
        }
        $price.textContent = formatPrice(data.price);
        $status.textContent = '';
      } catch (err) {
        if (mySeq !== priceSeq) return;
        $price.textContent = '—';
        $status.textContent = 'Network error.';
      }
    }

    function errorMessage(code) {
      switch (code) {
        case 'missing_params':         return 'Please choose size, thickness, and finish.';
        case 'not_found':              return 'No price for this combination.';
        case 'finish_not_allowed':     return 'This finish is not available for the selected material.';
        case 'material_not_eligible':  return 'This material is not Rubio-eligible.';
        default:                       return 'Price unavailable.';
      }
    }

    function formatPrice(n) {
      return '$' + Number(n).toFixed(2);
    }

    // ── Add to cart (mirrors slab's flow) ────────────────────────────
    // /api/configure → 5-sec Shopify sync gap (countdown on the button) →
    // /cart/add.js with 422-retry backoff → dispatch variant:add so Focal's
    // cart-drawer / notification-drawer opens. Helpers are duplicated from
    // slab's configurator.js (not extracted to a shared module) so any
    // change here can't affect slab.
    const ATC_SYNC_GAP_SECONDS    = 5;
    const ATC_REASSURE_INTERVAL_MS = 1500;
    const ATC_CART_ADD_BACKOFFS_MS = [500, 1500, 3000];
    const ATC_REASSURE_MSGS = [
      'Almost there…',
      'Syncing with Shopify…',
      'Just a moment more…',
      'Finalizing your order…',
      'Hang tight…',
      'Wrapping up…',
    ];
    const ATC_DEFAULT_LABEL = $atc.textContent || 'Add Corner Shelf to Cart';

    $form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const mat = currentMaterial();
      if (!mat) { $status.textContent = 'Material not configured.'; return; }

      setAtcBusy(true);
      setAtcLabel('Adding…');
      $status.textContent = '';

      try {
        const aIn = dimInches(state.aWhole, state.aFraction);
        const bIn = dimInches(state.bWhole, state.bFraction);
        const cIn = dimInches(state.cWhole, state.cFraction);
        if (aIn == null || bIn == null || cIn == null) {
          throw new Error('missing_params');
        }

        // Step 1 — create variant via our backend.
        const cfgRes = await fetch(cfg.apiUrl + '/api/configure', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            subtype:       'corner',
            product_id:    cfg.productId,
            material:      'material type ' + mat.tier,
            material_name: mat.name,
            finish:        state.finish,
            a_inches:      aIn,
            b_inches:      bIn,
            c_inches:      cIn,
            thickness:     state.thickness,
          }),
        });
        const data = await cfgRes.json().catch(() => ({}));
        if (!cfgRes.ok || !data.variant_id) {
          throw new Error(data.error || 'configure_failed');
        }

        $variant.value = data.variant_id;
        appendLineProps(data.price, mat);

        // Step 2 — 5s sync gap. Visible countdown on the button.
        await runAtcCountdown(ATC_SYNC_GAP_SECONDS);

        // Step 3 — Focal-native cart/add.js with reassurance rotation.
        startAtcReassurance();
        try { await focalCartAdd($form); }
        finally { stopAtcReassurance(); }

        // Focal opens its drawer/notification via the variant:add event
        // dispatched inside focalCartAdd. Nothing more to do here.
      } catch (err) {
        $status.textContent = friendlyAtcError(err);
      } finally {
        stopAtcReassurance();
        stopAtcCountdown();
        setAtcBusy(false);
        setAtcLabel(ATC_DEFAULT_LABEL);
      }
    }, /*useCapture*/ true);

    function setAtcBusy(busy) {
      $atc.disabled = !!busy;
      if (busy) $atc.setAttribute('aria-busy', 'true');
      else      $atc.removeAttribute('aria-busy');
    }
    function setAtcLabel(text) { $atc.textContent = text; }

    let __cnrCountdownTimer = null;
    function runAtcCountdown(seconds) {
      return new Promise((resolve) => {
        stopAtcCountdown();
        let n = seconds;
        $atc.textContent = 'Preparing… ' + n + 's';
        __cnrCountdownTimer = setInterval(() => {
          n -= 1;
          if (n <= 0) { stopAtcCountdown(); resolve(); return; }
          $atc.textContent = 'Preparing… ' + n + 's';
        }, 1000);
      });
    }
    function stopAtcCountdown() {
      if (__cnrCountdownTimer) { clearInterval(__cnrCountdownTimer); __cnrCountdownTimer = null; }
    }

    let __cnrReassureTimer = null;
    function startAtcReassurance() {
      stopAtcReassurance();
      let lastIdx = -1;
      const pick = () => {
        let i;
        do { i = Math.floor(Math.random() * ATC_REASSURE_MSGS.length); }
        while (i === lastIdx && ATC_REASSURE_MSGS.length > 1);
        lastIdx = i;
        $atc.textContent = ATC_REASSURE_MSGS[i];
      };
      pick();
      __cnrReassureTimer = setInterval(pick, ATC_REASSURE_INTERVAL_MS);
    }
    function stopAtcReassurance() {
      if (__cnrReassureTimer) { clearInterval(__cnrReassureTimer); __cnrReassureTimer = null; }
    }

    async function focalCartAdd(form) {
      const variantInput = form.querySelector('input[name="id"]');
      const variantId = variantInput ? variantInput.value : '';
      const sectionsToBundle = ['variant-added'];

      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      }));

      const formData = new FormData(form);
      formData.set('sections', sectionsToBundle.join(','));
      formData.set('sections_url', shopifyRoot() + 'variants/' + variantId);

      const addJson = await postCartAddWithBackoff(formData);

      const cartContent = await fetch(shopifyRoot() + 'cart.js', {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      }).then(r => r.json());
      cartContent.sections = addJson.sections;

      form.dispatchEvent(new CustomEvent('variant:add', {
        bubbles: true,
        detail: { items: addJson.items || [addJson], cart: cartContent },
      }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'variant:add', cart: cartContent },
      }));
    }

    async function postCartAddWithBackoff(formData) {
      const attempts = ATC_CART_ADD_BACKOFFS_MS.length + 1;
      let lastErr = null;
      for (let i = 0; i < attempts; i++) {
        const res = await fetch(shopifyRoot() + 'cart/add.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });
        if (res.ok) return res.json();
        const body = await res.json().catch(() => ({}));
        const err = new Error(body.description || body.message || 'cart_add_' + res.status);
        err.status = res.status; err.body = body;
        lastErr = err;
        if (res.status === 422 && i < attempts - 1) {
          await new Promise(r => setTimeout(r, ATC_CART_ADD_BACKOFFS_MS[i]));
          continue;
        }
        throw err;
      }
      throw lastErr;
    }

    function shopifyRoot() {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    }

    function friendlyAtcError(err) {
      if (err && err.status === 422) {
        return 'Cart is taking longer than usual to sync. Please click Add Corner Shelf to Cart again.';
      }
      const m = (err && err.message) || '';
      if (m === 'configure_failed' || m === 'price_not_found') {
        return "Couldn't create variant for this combination. Please adjust your configuration.";
      }
      return errorMessage(m) || 'Could not add to cart. Please try again.';
    }

    function appendLineProps(price, mat) {
      Array.from($form.querySelectorAll('input[data-cnr-prop]')).forEach(n => n.remove());

      const thickObj = cfg.thicknesses.find(t => t.code === state.thickness);
      const finObj   = cfg.finishOptions.find(o => o.id === state.finishChip);
      const buildObj = cfg.buildStyles.find(b => b.id === state.buildStyle);
      // Exact thickness only emitted for range-thickness selections —
      // workshop "order record" per Billy 2026-06-09.
      const exactThick = thicknessExactLabel();

      const props = {
        'Material':         mat.name,
        'A':                formatDim(state.aWhole, state.aFraction),
        'B':                formatDim(state.bWhole, state.bFraction),
        'C':                formatDim(state.cWhole, state.cFraction),
        'Thickness':        thickObj ? thickObj.label : state.thickness,
        'Exact Thickness':  exactThick || null,
        'Finish':           finObj   ? finObj.label   : state.finishChip,
        'Build Style':      buildObj ? buildObj.label : state.buildStyle,
        '_price':           Number(price).toFixed(2),
      };
      Object.keys(props).forEach(k => {
        if (!props[k]) return;
        const input = document.createElement('input');
        input.type  = 'hidden';
        input.name  = 'properties[' + k + ']';
        input.value = props[k];
        input.dataset.cnrProp = '1';
        $form.appendChild(input);
      });
    }

    // ── Build style chips (image cards) ──────────────────────────────
    function rebuildBuildStyles() {
      const prev = state.buildStyle;
      const visible = cfg.buildStyles || [];
      if (!visible.length) { $buildList.innerHTML = ''; return; }

      const def = visible.find(b => b.id === prev) || visible.find(b => b.isDefault) || visible[0];
      state.buildStyle = def.id;

      // Markup mirrors the slab build-style card pattern (.cfg-build):
      // 100px square image box with the native radio visually replaced by
      // a small check-indicator + name on a single row below. Keeps the
      // visual rhythm consistent between the top section and the corner
      // section without duplicating slab's eye-zoom modal (only 2 chips
      // here — the modal is overkill).
      // Build-style chips are pure selection — simple Inside / Outside
      // Mitered GIFs. The labeled A/B/C diagrams live in the dimensions
      // help popup (ⓘ icons next to A and B). A small eye-zoom button
      // sits on each chip so the customer can enlarge the chip GIF in
      // the shared lightbox — same UX as the slab and hook configurator
      // build-style chips. The button's data-xgal-zoom + data-xgal-src
      // attributes are picked up by extras-gallery-rotate.js's single-
      // image modal delegate (Focal's product-zoom-button is gallery-
      // scoped and not appropriate for a one-shot chip image).
      const idPrefix = 'cnr-build-' + (cfg.sectionId || 'x');
      $buildList.innerHTML = visible.map(s => {
        const inputId = idPrefix + '-' + s.id;
        const checked = (s.id === state.buildStyle) ? ' checked' : '';
        return (
          '<label class="cnr-build" data-cnr-build="' + esc(s.id) + '" for="' + inputId + '">'
            + '<span class="cnr-build__box">'
              + '<img class="cnr-build__img" src="' + esc(s.image || '') + '" alt="' + esc(s.label) + '">'
              + (s.image
                ? '<button type="button" class="cnr-build__zoom" data-xgal-zoom data-xgal-src="' + esc(s.image) + '" data-xgal-alt="' + esc(s.label) + '" aria-label="Enlarge ' + esc(s.label) + '">'
                    + '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
                      + '<circle cx="11" cy="11" r="7"></circle>'
                      + '<line x1="21" y1="21" x2="16.65" y2="16.65"></line>'
                      + '<line x1="11" y1="8" x2="11" y2="14"></line>'
                      + '<line x1="8"  y1="11" x2="14" y2="11"></line>'
                    + '</svg>'
                  + '</button>'
                : '')
            + '</span>'
            + '<span class="cnr-build__check">'
              + '<input type="radio" name="cnr-build-style" id="' + inputId + '" value="' + esc(s.id) + '"' + checked + '>'
              + '<span class="cnr-build__indicator"></span>'
              + '<span class="cnr-build__name">' + esc(s.label) + '</span>'
            + '</span>'
          + '</label>'
        );
      }).join('');
    }

    // ── Dimensions help modal (ⓘ icons next to A and B) ─────────────
    // Shows both labeled diagrams (Inside + Outside) stacked vertically
    // so the customer can compare and identify what A, B, C mean on
    // each build style. Reuses the .cfg-modal CSS family — same visual
    // pattern as the slab build-zoom and section-image-zoom modals —
    // but lives as its own element so the content is dedicated to the
    // diagrams and doesn't share state with the zoom modal.
    let __cnrHelpModal = null;
    function ensureHelpModal() {
      if (__cnrHelpModal) return __cnrHelpModal;
      const inside  = (cfg.diagrams && cfg.diagrams.inside)  || '';
      const outside = (cfg.diagrams && cfg.diagrams.outside) || '';
      const m = document.createElement('div');
      m.className = 'cfg-modal cnr-help-modal';
      m.setAttribute('role', 'dialog');
      m.setAttribute('aria-modal', 'true');
      m.setAttribute('aria-label', 'Corner shelf dimensions guide');
      m.hidden = true;
      m.innerHTML =
        '<div class="cfg-modal__backdrop" data-cnr-help-close></div>' +
        '<div class="cfg-modal__dialog cnr-help-modal__dialog">' +
          '<button type="button" class="cfg-modal__close" data-cnr-help-close aria-label="Close">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true">' +
              '<line x1="6" y1="6" x2="18" y2="18"></line>' +
              '<line x1="18" y1="6" x2="6" y2="18"></line>' +
            '</svg>' +
          '</button>' +
          '<div class="cnr-help-modal__body">' +
            '<figure class="cnr-help-modal__figure">' +
              '<img src="' + esc(inside)  + '" alt="Inside Corner Mitered Floating Shelves">' +
            '</figure>' +
            '<figure class="cnr-help-modal__figure">' +
              '<img src="' + esc(outside) + '" alt="Outside Corner Mitered Floating Shelves">' +
            '</figure>' +
          '</div>' +
        '</div>';
      document.body.appendChild(m);
      m.addEventListener('click', (e) => {
        if (e.target.closest('[data-cnr-help-close]')) closeHelpModal();
      });
      __cnrHelpModal = m;
      return m;
    }
    function openHelpModal() {
      const m = ensureHelpModal();
      m.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      m.__keyHandler = (e) => { if (e.key === 'Escape') closeHelpModal(); };
      document.addEventListener('keydown', m.__keyHandler);
      m.querySelector('.cfg-modal__close').focus();
    }
    function closeHelpModal() {
      if (!__cnrHelpModal || __cnrHelpModal.hidden) return;
      __cnrHelpModal.hidden = true;
      document.documentElement.style.overflow = '';
      if (__cnrHelpModal.__keyHandler) {
        document.removeEventListener('keydown', __cnrHelpModal.__keyHandler);
        __cnrHelpModal.__keyHandler = null;
      }
    }

    // ── Wiring ───────────────────────────────────────────────────────
    function wireDim(wholeEl, fracEl, wholeKey, fracKey, priceImpacting) {
      wholeEl.addEventListener('change', () => {
        state[wholeKey] = wholeEl.value;
        applyAllCeilingRules();
        // Keep state's fraction in sync if the rule snapped it to "0".
        state[fracKey] = fracEl.value;
        pushStateToURL();
        if (priceImpacting) schedulePrice();
      });
      fracEl.addEventListener('change', () => {
        state[fracKey] = fracEl.value;
        pushStateToURL();
        if (priceImpacting) schedulePrice();
      });
    }
    wireDim($aW, $aF, 'aWhole', 'aFraction', true);
    wireDim($bW, $bF, 'bWhole', 'bFraction', true);
    // C is depth, not price-affecting, but still write URL state +
    // ceiling rules so share-links reproduce the exact selection.
    wireDim($cW, $cF, 'cWhole', 'cFraction', false);

    $thickSel.addEventListener('change', () => {
      state.thickness = $thickSel.value;
      refreshThicknessExact();
      pushStateToURL();
      schedulePrice();
    });
    if ($thickExactW) $thickExactW.addEventListener('change', () => {
      state.thickExactW = $thickExactW.value;
      const range = parseThicknessRange(state.thickness);
      if (range) {
        const minWhole = Math.floor(range.low);
        const maxWhole = Math.floor(range.high);
        const floorExclusive = (range.low > Math.floor(range.low));
        applyCeilingRule($thickExactW, $thickExactF, minWhole, maxWhole, floorExclusive);
        state.thickExactF = $thickExactF.value;
      }
      pushStateToURL();
    });
    if ($thickExactF) $thickExactF.addEventListener('change', () => {
      state.thickExactF = $thickExactF.value;
      pushStateToURL();
    });
    $finList.addEventListener('change', (e) => {
      const input = e.target.closest('[data-cnr-finish-input]');
      if (!input) return;
      // Enforce radio-group behavior over checkboxes (matches slab pattern):
      // only the just-clicked chip stays checked.
      $finList.querySelectorAll('[data-cnr-finish-input]').forEach(el => {
        el.checked = (el === input);
      });
      state.finishChip = input.value;
      const opt = cfg.finishOptions.find(o => o.id === state.finishChip);
      if (opt) state.finish = opt.finish;
      pushStateToURL();
      schedulePrice();
    });
    $buildList.addEventListener('change', (e) => {
      const input = e.target.closest('input[name="cnr-build-style"]');
      if (!input) return;
      state.buildStyle = input.value;
      pushStateToURL();
      // Build style is line-item-only — no /api/price refresh required.
    });

    // ── Init ─────────────────────────────────────────────────────────
    fillRangeSelect($aW, cfg.aRange.min, cfg.aRange.max);
    fillRangeSelect($bW, cfg.bRange.min, cfg.bRange.max);
    fillRangeSelect($cW, cfg.cRange.min, cfg.cRange.max);
    fillFractions($aF);
    fillFractions($bF);
    fillFractions($cF);

    readStateFromURL();
    rebuildFinishOptions();
    rebuildBuildStyles();
    $thickSel.value = state.thickness;
    $aW.value = state.aWhole; $aF.value = state.aFraction;
    $bW.value = state.bWhole; $bF.value = state.bFraction;
    $cW.value = state.cWhole; $cF.value = state.cFraction;
    applyAllCeilingRules();
    refreshThicknessExact();

    // ⓘ icons next to A and B open the dimensions help modal.
    root.querySelectorAll('[data-cnr-info]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        openHelpModal();
      });
    });

    schedulePrice();
  }
})();
